import { safeParseSchemaWithSentry } from "@/features/Sentry/utils";
import { getAuthorized } from "@/lib/fetch";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TSections } from "./Fields";

export type TTranslation = {
  [class_name: string]: {
    [translation_id: string]: string;
  };
};

/**
 * Custom hook to fetch an item from the database and handle translations.
 *
 * This hook performs two main operations:
 * 1. Fetches an item from the database based on its ID
 * 2. Fetches translations for the item's fields
 *
 * The hook also handles data validation using Zod and applies translations to the fetched data.
 * When creating a new item (isNew=true), the data fetching is disabled but translations are still loaded.
 *
 * @param {number} id - The ID of the item to fetch
 * @param {string} client - The client identifier for API requests
 * @param {string} tableName - The database table name for the item
 * @param {string} language - The language code for translations
 * @param {z.ZodSchema<any>} schema - Zod schema for validating the fetched data
 * @param {ReadonlyArray<string>} [fieldsNotApplyTranslation] - Fields that should not be translated
 * @param {boolean} [isNew=false] - Whether this is a new item (if true, data fetching is disabled)
 * @returns {Object} The fetched data and related information
 * @returns {any} returns.data - The fetched and translated data
 * @returns {TTranslation} returns.translations - The translations mapping
 * @returns {boolean} returns.isLoading - Whether data is currently being loaded
 * @returns {any} returns.error - Any error that occurred during fetching
 */
function useFetchItem({
  id,
  client,
  tableName,
  language,
  schema,
  fieldsNotApplyTranslation,
  isNew = false,
  disable_translations_request,
}: {
  id: number | string;
  client: string;
  tableName: string;
  language: string;
  schema: z.ZodSchema<any>;
  fieldsNotApplyTranslation?: ReadonlyArray<string>;
  isNew?: boolean;
  disable_translations_request?: boolean;
}) {
  // Fetch item data
  const { data, error, isLoading } = useQuery({
    queryKey: [client, tableName, "get", "item", id],
    queryFn: () =>
      getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/${tableName}/${id}`,
      )
        .then((res) => res.json())
        .then((resData) => {
          const parsedData = safeParseSchemaWithSentry(schema, resData);
          if (parsedData.success) {
            return parsedData.data;
          }
          // Fall back to the raw payload instead of blocking the whole form:
          // a field that fails validation (e.g. a malformed ISIN loaded by
          // ETL) still needs to be editable so the user can fix it. Sentry
          // already captured the validation failure above, and save-time
          // validation (zodResolver on submit) still enforces the schema.
          console.error("Zod validation errors:", parsedData.error.issues);
          return resData;
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          throw error;
        }),
    enabled: !isNew,
    refetchOnWindowFocus: false,
  });

  // Fetch translations
  const { data: translations, isLoading: isLoadingTranslations } =
    useQuery<TTranslation>({
      queryKey: [tableName, "translations", language],
      queryFn: async () => {
        const res = await getAuthorized(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/${client}/${tableName}/translations?language=${language}`,
        );
        return res.json();
      },
      enabled: !disable_translations_request,
      refetchOnWindowFocus: false,
    });

  // Apply translations to the item data
  const translatedData = useMemo(() => {
    if (!data) return null;
    if (!translations) return data;

    const newData = { ...data };

    Object.entries(translations).forEach(([column, columnTranslations]) => {
      const value = data[column];
      if (
        typeof value === "string" &&
        typeof columnTranslations === "object" &&
        columnTranslations !== null &&
        (columnTranslations as Record<string, any>)[value] &&
        // if we don't want to convert the key into the translation
        fieldsNotApplyTranslation &&
        !fieldsNotApplyTranslation.includes(column)
      ) {
        newData[column] = (columnTranslations as Record<string, any>)[value];
      }
    });

    return newData;
  }, [data, translations]);

  return {
    data: translatedData,
    translations: translations,
    isLoading: isLoading || isLoadingTranslations,
    error,
  };
}

type TTranslatedFieldLabel = string;

type SectionOption = {
  section_name: string;
  fields?: ReadonlyArray<TTranslatedFieldLabel>;
};

type SectionOptions = ReadonlyArray<SectionOption>;

type FlatOption = {
  label: string;
  value: string;
};

type FlatOptions = ReadonlyArray<FlatOption>;

// Type conditionnel pour adjustOptions en fonction du type d'entrée
type AdjustOptionsFunc<T> = T extends SectionOptions
  ? (
      options: SectionOptions,
      translation: { [translation_id: string]: string },
    ) => SectionOptions
  : (
      options: FlatOptions,
      translation: { [translation_id: string]: string },
    ) => FlatOptions;

export type DynamicOptionsInput<
  T extends SectionOptions | FlatOptions = SectionOptions | FlatOptions,
> = {
  translation?: {
    [translation_id: string]: string;
  };
  options: T;
  adjustOptions?: AdjustOptionsFunc<T>;
  sectionNameTranslationPrefix?: string;
  // If deps is provided, the options will be rebuilt when the deps change by calling adjustOptions
  deps: ReadonlyArray<any>;
};

export type DynamicOptionsOutput = {
  options: Array<FlatOption> | TSections;
};

/**
 * Hook that builds dynamic options for selects and other form components
 *
 * @param translation - Dictionary of translation strings indexed by key
 * @param options - Either an array of sections with fields, or a flattened array of label/value pairs
 * @param adjustOptions - Optional function to adjust/filter options before returning them
 * @param deps - Dependencies array that will trigger options rebuild when changed
 * @returns An object containing options in the appropriate format (flat list or sections)
 */
export function useDynamicOptionsBuilder<
  T extends SectionOptions | FlatOptions,
>({
  translation,
  options,
  adjustOptions,
  sectionNameTranslationPrefix,
  deps,
}: DynamicOptionsInput<T>): DynamicOptionsOutput {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    // If translation is not available, return empty array
    if (!translation) {
      return { options: [] };
    }

    // Apply adjustOptions if provided, with appropriate type cast
    const adjustedOptions = adjustOptions
      ? (adjustOptions(options as any, translation) as T)
      : options;

    // Process options with section_name structure
    if (adjustedOptions.length > 0 && "section_name" in adjustedOptions[0]) {
      const sections: TSections = (adjustedOptions as SectionOptions)
        .map((section) => {
          const sectionLabel = sectionNameTranslationPrefix
            ? t(`${sectionNameTranslationPrefix}.${section.section_name}`, {
                defaultValue: section.section_name,
              })
            : translation[section.section_name] || section.section_name;

          // Create section items
          const items: Array<FlatOption> = [];

          if (section.fields && section.fields.length > 0) {
            section.fields.forEach((field) => {
              items.push({
                label: translation[field] || field,
                value: field,
              });
            });
          }

          return {
            key: section.section_name,
            value: sectionLabel,
            items: items,
          };
        })
        .filter((section) => section.items.length > 0);

      return { options: sections };
    }

    // If options are already in {label, value} format, convert from readonly to mutable
    const flatOptions = Array.from(adjustedOptions as FlatOptions);
    return { options: flatOptions };
  }, [options, translation, sectionNameTranslationPrefix, t, i18n.language, ...deps]);
}

export { useFetchItem };
